<template>
  <modal name="modal-trip-share" height="auto" :scrollable="true" :adaptive="true" width="600px">
    <div class="modal-header">
      <h3 class="text-lg font-semibold text-white">{{ $t('trip_share.title') }}</h3>
      <button @click="closeModal" class="text-white hover:text-gray-200">
        <BaseIcon icon="times" />
      </button>
    </div>

    <div class="modal-body">
      <!-- Email Section -->
      <div class="form-group mb-4">
        <div class="flex items-center gap-3 mb-3">
          <button 
            @click="showEmailInput = !showEmailInput"
            :class="['toggle-btn', { 'toggle-active': showEmailInput }]"
            type="button"
          >
            <span class="toggle-slider"></span>
          </button>
          <span class="text-sm font-semibold text-gray-600">{{ $t('trip_share.share_via_email') }}</span>
        </div>
        <div class="flex gap-2">
          <input 
            v-model="recipientEmail" 
            type="email" 
            class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            :class="{ 'opacity-50 cursor-not-allowed': !showEmailInput }"
            :placeholder="$t('trip_share.email_input_placeholder')"
            :disabled="!showEmailInput"
            @keyup.enter="addEmail"
          />
          <button 
            @click="addEmail" 
            class="btn-blue-outline px-4 py-2 whitespace-nowrap"
            :disabled="!showEmailInput || !recipientEmail"
            :class="{ 'opacity-50 cursor-not-allowed': !showEmailInput }"
          >
            <BaseIcon icon="plus" class="mr-1" />
            {{ $t('trip_share.add_button') }}
          </button>
        </div>
        <p v-if="emailError" class="text-red-500 text-xs mt-1" :class="{ 'opacity-50': !showEmailInput }">{{ emailError }}</p>
        
        <!-- Email Recipients List -->
        <div v-if="emailList.length > 0" class="mt-3 flex flex-wrap gap-2" :class="{ 'opacity-50': !showEmailInput }">
          <div 
            v-for="(email, index) in emailList" 
            :key="'email-' + index"
            class="recipient-chip bg-blue-100 text-blue-800"
          >
            <span>{{ email }}</span>
            <button @click="removeEmail(index)" :disabled="!showEmailInput" class="ml-2 text-blue-600 hover:text-blue-900" :class="{ 'cursor-not-allowed': !showEmailInput }" title="Remove">
              ✕
            </button>
          </div>
        </div>
      </div>

      <!-- SMS Toggle -->
      <div class="form-group mb-4">
        <div class="flex items-center gap-3">
          <button 
            @click="showSmsInput = !showSmsInput"
            :class="['toggle-btn', { 'toggle-active': showSmsInput }]"
            type="button"
          >
            <span class="toggle-slider"></span>
          </button>
          <span class="text-sm font-semibold text-gray-600">{{ $t('trip_share.share_via_sms') }}</span>
        </div>
      </div>

      <!-- Phone Section -->
      <div class="form-group mb-4">
        <div class="flex gap-2">
          <input 
            v-model="recipientPhone" 
            type="tel" 
            class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            :class="{ 'border-red-500': phoneInputError, 'border-green-500': recipientPhone && !phoneInputError && recipientPhone.length > 5, 'opacity-50 cursor-not-allowed': !showSmsInput }"
            :placeholder="$t('trip_share.phone_input_placeholder')"
            :disabled="!showSmsInput"
            @keyup.enter="addPhone"
            @input="validatePhoneInput"
          />
          <button 
            @click="addPhone" 
            class="btn-blue-outline px-4 py-2 whitespace-nowrap"
            :disabled="!showSmsInput || !recipientPhone || !!phoneInputError"
            :class="{ 'opacity-50 cursor-not-allowed': !showSmsInput }"
          >
            <BaseIcon icon="plus" class="mr-1" />
            {{ $t('trip_share.add_button') }}
          </button>
        </div>
        <p v-if="phoneError" class="text-red-500 text-xs mt-1" :class="{ 'opacity-50': !showSmsInput }">{{ phoneError }}</p>
        <p v-else-if="phoneInputError" class="text-orange-600 text-xs mt-1" :class="{ 'opacity-50': !showSmsInput }">{{ phoneInputError }}</p>
        <p v-else-if="recipientPhone && !phoneInputError && recipientPhone.length > 5" class="text-green-600 text-xs mt-1" :class="{ 'opacity-50': !showSmsInput }">{{ $t('trip_share.phone_valid') }}</p>
        
        <!-- Phone Recipients List -->
        <div v-if="phoneList.length > 0" class="mt-3 flex flex-wrap gap-2" :class="{ 'opacity-50': !showSmsInput }">
          <div 
            v-for="(phone, index) in phoneList" 
            :key="'phone-' + index"
            class="recipient-chip bg-green-100 text-green-800"
          >
            <span>{{ phone }}</span>
            <button @click="removePhone(index)" :disabled="!showSmsInput" class="ml-2 text-green-600 hover:text-green-900" :class="{ 'cursor-not-allowed': !showSmsInput }" title="Remove">
              ✕
            </button>
          </div>
        </div>
      </div>

      <!-- Horizontal Stepper -->
      <div class="stepper-container">
        <div class="stepper-line"></div>
        <div class="stepper-text">
          <span class="stepper-label">{{ $t('trip_share.or_separator') }}</span>
        </div>
      </div>

      <!-- Modern Share Link Section -->
      <div class="share-link-section">
        <div class="share-link-header">
          <h4 class="share-link-title">{{ $t('trip_share.trip_tracking_link') }}</h4>
        </div>

        <div class="link-display-container">
          <div class="link-input-wrapper">
            <input
              :value="shareLink"
              readonly
              class="modern-link-input"
              ref="linkInput"
            />
            <div class="link-actions">
              <button
                @click="copyLink"
                class="copy-button-primary"
                :class="{ 'copied': linkCopied }"
              >
                <BaseIcon :icon="linkCopied ? 'check' : 'copy'" class="copy-icon" />
                <span class="copy-text">{{ linkCopied ? 'Copied!' : $t('trip_share.copy') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Access Code Section -->
      <div v-if="otp" class="access-code-section">
        <div class="access-code-header">
          <h4 class="access-code-title">{{ $t('trip_share.access_code') }}</h4>
        </div>
        <p class="access-code-desc">{{ $t('trip_share.access_code_desc') }}</p>
        <div class="access-code-display">
          <span class="access-code-value">{{ otp }}</span>
          <button
            @click="copyOtp"
            class="copy-button-primary"
            :class="{ 'copied': otpCopied }"
          >
            <BaseIcon :icon="otpCopied ? 'check' : 'copy'" class="copy-icon" />
            <span class="copy-text">{{ otpCopied ? $t('trip_share.copied') : $t('trip_share.copy') }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button @click="closeModal" class="btn-red-outline px-4 py-2">
        {{ $t('user_actions.cancel') }} <BaseIcon icon="times-circle" class="ml-1" />
      </button>
      <button 
        @click="sendShareLink" 
        :disabled="isLoading || (emailList.length === 0 && phoneList.length === 0) || !shareLink" 
        class="btn-blue px-4 py-2"
      >
        <BaseIcon v-if="isLoading" icon="spinner" class="fa-spin mr-2" />
        <BaseIcon v-else icon="paper-plane" class="mr-2" />
        {{ isLoading ? $t('trip_share.sending') : $t('trip_share.send_to_count', { count: (emailList.length + phoneList.length), recipient: (emailList.length + phoneList.length) === 1 ? $t('trip_share.person') : $t('trip_share.people') }) }}
      </button>
    </div>
  </modal>
</template>

<script>
import moment from 'moment-timezone';

export default {
  name: 'TripShare',
  
  data() {
    return {
      trip: null,
      recipientEmail: '',
      recipientPhone: '',
      emailList: [],
      phoneList: [],
      emailError: '',
      phoneError: '',
      phoneInputError: '',
      shareLink: '',
      isLoading: false,
      showSmsInput: false,
      showEmailInput: true,
      linkCopied: false,
      otp: null,
      otpCopied: false,
    };
  },

  watch: {
    showEmailInput(newValue) {
      if (!newValue) {
        // Reset email list when email sharing is disabled
        this.emailList = [];
        this.recipientEmail = '';
        this.emailError = '';
      }
    },
    showSmsInput(newValue) {
      if (!newValue) {
        // Reset phone list when SMS sharing is disabled
        this.phoneList = [];
        this.recipientPhone = '';
        this.phoneError = '';
        this.phoneInputError = '';
      }
    }
  },

  methods: {
    async setupModal(trip) {
      this.trip = trip;
      this.recipientEmail = '';
      this.recipientPhone = '';
      this.emailList = [];
      this.phoneList = [];
      this.emailError = '';
      this.phoneError = '';
      this.phoneInputError = '';
      this.isLoading = false;
      this.showSmsInput = false;
      this.otp = null;
      this.otpCopied = false;
      
      // Generate link from backend
      await this.getLink();
      
    },

    closeModal() {
      this.$modal.hide('modal-trip-share');
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    validatePhone(phone) {
      // Remove spaces and check if it's a valid phone format
      const cleaned = phone.replace(/[\s-()]/g, '');
      
      // Accept phone with country code (+XXX...) or without (local number)
      // With country code: +[1-9]... (10-15 digits total)
      // Without country code: starts with 0 or 1-9 (8-15 digits)
      const withCountryCode = /^\+[1-9]\d{9,14}$/;
      const withoutCountryCode = /^[0-9]\d{7,14}$/;
      
      return withCountryCode.test(cleaned) || withoutCountryCode.test(cleaned);
    },

    validatePhoneInput() {
      this.phoneInputError = '';
      
      if (!this.recipientPhone) {
        return;
      }

      const cleaned = this.recipientPhone.replace(/[\s-()]/g, '');
      const digitsOnly = cleaned.replace(/\+/g, '');
      const hasCountryCode = this.recipientPhone.trim().startsWith('+');
      
      // Check if contains only valid characters
      if (!/^[\d\s+()-]*$/.test(this.recipientPhone)) {
        this.phoneInputError = this.$t('trip_share.phone_invalid_chars');
        return;
      }

      // If it has + but no digits after, show error
      if (hasCountryCode && digitsOnly.length === 0) {
        this.phoneInputError = this.$t('trip_share.phone_need_digits');
        return;
      }

      // Check minimum length based on format
      if (hasCountryCode) {
        // With country code: minimum 10 digits (excluding +)
        if (digitsOnly.length > 0 && digitsOnly.length < 10) {
          this.phoneInputError = this.$t('trip_share.phone_too_short_country');
          return;
        }
      } else {
        // Without country code: minimum 8 digits for local numbers
        if (digitsOnly.length > 0 && digitsOnly.length < 8) {
          this.phoneInputError = this.$t('trip_share.phone_too_short');
          return;
        }
      }

      // Check maximum length (digits only, excluding +)
      if (digitsOnly.length > 15) {
        this.phoneInputError = this.$t('trip_share.phone_too_long');
        return;
      }
    },

    addEmail() {
      this.emailError = '';
      
      if (!this.recipientEmail.trim()) {
        return;
      }

      if (!this.validateEmail(this.recipientEmail)) {
        this.emailError = this.$t('trip_share.email_invalid') || 'Please enter a valid email address';
        return;
      }

      // Check for duplicates
      if (this.emailList.includes(this.recipientEmail.trim())) {
        this.emailError = this.$t('trip_share.email_already_added');
        return;
      }

      this.emailList.push(this.recipientEmail.trim());
      this.recipientEmail = '';
    },

    removeEmail(index) {
      this.emailList.splice(index, 1);
    },

    addPhone() {
      this.phoneError = '';
      this.phoneInputError = '';
      
      if (!this.recipientPhone.trim()) {
        return;
      }

      if (!this.validatePhone(this.recipientPhone)) {
        this.phoneError = this.$t('trip_share.phone_required');
        return;
      }

      // Normalize phone number
      const normalizedPhone = this.recipientPhone.replace(/[\s-()]/g, '');
      
      // Check for duplicates - including variations with/without country code
      const isDuplicate = this.phoneList.some(existingPhone => {
        // Direct match
        if (existingPhone === normalizedPhone) {
          return true;
        }
        
        // Check if one is the local version of the other
        // E.g., "01640888424" vs "+8801640888424"
        const existingDigits = existingPhone.replace(/\+/g, '');
        const newDigits = normalizedPhone.replace(/\+/g, '');
        
        // If one starts with + and the other doesn't
        if (existingPhone.startsWith('+') && !normalizedPhone.startsWith('+')) {
          // Check if adding country code makes them same
          // Remove leading 0 from local number and compare
          const localWithoutZero = newDigits.replace(/^0+/, '');
          if (existingDigits.endsWith(localWithoutZero)) {
            return true;
          }
        } else if (!existingPhone.startsWith('+') && normalizedPhone.startsWith('+')) {
          // Check if removing country code makes them same
          const existingWithoutZero = existingDigits.replace(/^0+/, '');
          if (newDigits.endsWith(existingWithoutZero)) {
            return true;
          }
        }
        
        return false;
      });

      if (isDuplicate) {
        this.phoneError = this.$t('trip_share.phone_already_added');
        return;
      }

      this.phoneList.push(normalizedPhone);
      this.recipientPhone = '';
      this.phoneInputError = '';
    },

    removePhone(index) {
      this.phoneList.splice(index, 1);
    },
    async getLink() {
      try {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const requestData = {
          trip_id: this.trip._id
        };
        
        if (isLocalhost) {
          requestData.client_url = window.location.origin;
        }
        
        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/asset-tracker/trip/share/generate-share-link`, 
          requestData
        );
        
        if (response.data && response.data.share_link) {
          this.shareLink = response.data.share_link;
          this.otp = response.data.otp || null;
          this.otpCopied = false;
        } else {
          this.handleError(this.$t('trip_share.link_generation_failed'));
        }
      } catch (error) {
        console.error('Error generating share link:', error);
        this.handleError(error);
      }
    },

    async sendShareLink() {
      this.emailError = '';
      this.phoneError = '';

      // Check if at least one recipient is added
      if (this.emailList.length === 0 && this.phoneList.length === 0) {
        this.handleError(this.$t('trip_share.at_least_one_recipient'));
        return;
      }

      this.isLoading = true;

      try {
        // Send to all recipients
        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/asset-tracker/trip/share/send-email-sms`,
          {
            trip_id: this.trip._id,
            recipient_emails: this.emailList.length > 0 ? this.emailList : null,
            recipient_phones: this.phoneList.length > 0 ? this.phoneList : null,
          }
        );

        const totalRecipients = this.emailList.length + this.phoneList.length;
        this.handleSuccess(this.$t('trip_share.success_message', { count: totalRecipients }));
        
        // Refresh OTP in case it was regenerated on send
        if (response.data && response.data.otp) {
          this.otp = response.data.otp;
          this.otpCopied = false;
        }
        
        // Emit event to parent
        this.$emit('trip-shared', {
          trip: this.trip,
          emails: this.emailList,
          phones: this.phoneList,
          shareLink: this.shareLink
        });

        // Close modal after success
        setTimeout(() => {
          this.closeModal();
        }, 1500);

      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    copyOtp() {
      if (!this.otp) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.otp).then(() => {
          this.otpCopied = true;
          setTimeout(() => { this.otpCopied = false; }, 2000);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = this.otp;
        ta.style.position = 'fixed'; ta.style.left = '-999999px';
        document.body.appendChild(ta); ta.select();
       
        document.execCommand('copy'); this.otpCopied = true; setTimeout(() => { this.otpCopied = false; }, 2000);
          
        document.body.removeChild(ta);
      }
    },

    copyLink() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.shareLink).then(() => {
          this.showCopyFeedback();
        });
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = this.shareLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.showCopyFeedback();
        } catch (err) {
          this.handleError(this.$t('trip_share.copy_failed'));
        }
        document.body.removeChild(textArea);
      }
    },

    showCopyFeedback() {
      this.linkCopied = true;
      this.handleSuccess(this.$t('trip_share.link_copied'));

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        this.linkCopied = false;
      }, 2000);
    },

    formatDate(date) {
      return moment(date).tz('Europe/Stockholm').format('YYYY-MM-DD, HH:mm');
    }
  }
};
</script>

<style scoped>
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #3b82f6;
  color: #ffffff;
}

.modal-body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
}

.recipient-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.recipient-chip:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.recipient-chip button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.recipient-chip button:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

/* Toggle Button Styles */
.toggle-btn {
  width: 44px;
  height: 22px;
  background-color: #d1d5db;
  border: none;
  border-radius: 11px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  display: flex;
  align-items: center;
}

.toggle-btn.toggle-active {
  background-color: #3b82f6;
}

.toggle-slider {
  width: 18px;
  height: 18px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  left: 2px;
  transition: left 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.toggle-btn.toggle-active .toggle-slider {
  left: 24px;
}

/* Horizontal Stepper Styles */
.stepper-container {
  position: relative;
  display: flex;
  align-items: center;
  margin: 32px 0;
  width: 100%;
}

.stepper-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #e5e7eb 0%, #1A8DE9 50%, #e5e7eb 100%);
  position: relative;
}

.stepper-line::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #1A8DE9;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 2px 4px rgba(26, 141, 233, 0.3);
}

.stepper-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 2px 16px;
  border-radius: 20px;
  border: 2px solid #1A8DE9;
  box-shadow: 0 2px 8px rgba(26, 141, 233, 0.15);
}

.stepper-label {
  font-size: 14px;
  font-weight: 600;
  color: #1A8DE9;
  white-space: nowrap;
  letter-spacing: -0.025em;
}

/* Modern Share Link Section */

.share-link-header {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.share-link-title {
  color: #1A8DE9;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.025em;
}


.link-display-container {
  margin-bottom: 0;
}

.link-input-wrapper {
  position: relative;
  background: white;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.modern-link-input {
  width: 100%;
  padding: 12px 100px 12px 12px;
  border: none;
  outline: none;
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
  word-break: break-all;
}

.modern-link-input::selection {
  background: rgba(26, 141, 233, 0.2);
}

.link-actions {
  position: absolute;
  right: 6px;
  top: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
}

.copy-button-primary {
  background: linear-gradient(135deg, #1A8DE9 0%, #3B82F6 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(26, 141, 233, 0.25);
}

.copy-button-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(26, 141, 233, 0.35);
}

.copy-button-primary.copied {
  background: linear-gradient(135deg, #00C16E 0%, #10B981 100%);
  box-shadow: 0 2px 6px rgba(0, 193, 110, 0.25);
}

.copy-button-primary.copied:hover {
  box-shadow: 0 3px 10px rgba(0, 193, 110, 0.35);
}

.copy-icon {
  font-size: 14px;
}

.copy-text {
  white-space: nowrap;
}

/* Disabled Button Styles */
button:disabled {
  cursor: not-allowed !important;
}

.btn-blue-outline:disabled {
  cursor: not-allowed !important;
}

button:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Access Code Section */
.access-code-section {
  margin-top: 20px;
  background: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 15px;
}

.access-code-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.access-code-key-icon {
  color: #d97706;
  font-size: 16px;
}

.access-code-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  margin: 0;
}

.access-code-desc {
  font-size: 12px;
  color: #78350f;
  margin: 0 0 14px;
  line-height: 1.5;
}

.access-code-display {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 10px;
}

.access-code-value {
  flex: 1;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 8px;
  color: #1d4ed8;
  font-family: monospace;
  text-align: center;
}
</style>
